import { useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, Pencil, Trash2, GripVertical, Image as ImageIcon } from "lucide-react";
import { useAdminHeroSlides, useDeleteHeroSlide, useUpdateHeroSlide, HeroSlide } from "@/hooks/useHeroSlides";
import { HeroSlideFormDialog } from "@/components/admin/HeroSlideFormDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";

const AdminHeroSlides = () => {
  const { data: slides, isLoading } = useAdminHeroSlides();
  const deleteMutation = useDeleteHeroSlide();
  const updateMutation = useUpdateHeroSlide();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState<HeroSlide | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [slideToDelete, setSlideToDelete] = useState<string | null>(null);
  const [draggedItem, setDraggedItem] = useState<HeroSlide | null>(null);

  const handleEdit = (slide: HeroSlide) => {
    setSelectedSlide(slide);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setSelectedSlide(undefined);
    setDialogOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setSlideToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (slideToDelete) {
      await deleteMutation.mutateAsync(slideToDelete);
      setDeleteDialogOpen(false);
      setSlideToDelete(null);
    }
  };

  const handleDragStart = (e: React.DragEvent, slide: HeroSlide) => {
    setDraggedItem(slide);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e: React.DragEvent, targetSlide: HeroSlide) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetSlide.id) {
      setDraggedItem(null);
      return;
    }

    const draggedOrder = draggedItem.sort_order;
    const targetOrder = targetSlide.sort_order;

    await updateMutation.mutateAsync({
      id: draggedItem.id,
      sort_order: targetOrder,
    });

    await updateMutation.mutateAsync({
      id: targetSlide.id,
      sort_order: draggedOrder,
    });

    setDraggedItem(null);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p>Loading hero slides...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Hero Slides</h1>
            <p className="text-muted-foreground mt-2">
              Manage the hero slider images and content on the homepage
            </p>
          </div>
          <Button onClick={handleAdd}>
            <Plus className="mr-2 h-4 w-4" />
            Add Slide
          </Button>
        </div>

        <div className="grid gap-4">
          {slides?.length === 0 ? (
            <Card className="p-12 text-center">
              <ImageIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No hero slides yet</h3>
              <p className="text-muted-foreground mb-4">
                Get started by adding your first hero slide
              </p>
              <Button onClick={handleAdd}>
                <Plus className="mr-2 h-4 w-4" />
                Add First Slide
              </Button>
            </Card>
          ) : (
            slides?.map((slide) => (
              <Card
                key={slide.id}
                draggable
                onDragStart={(e) => handleDragStart(e, slide)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, slide)}
                className="p-4 cursor-move hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="flex items-center justify-center text-muted-foreground">
                    <GripVertical className="h-5 w-5" />
                  </div>

                  <img
                    src={slide.image_url}
                    alt={slide.title}
                    className="w-32 h-20 object-cover rounded"
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg truncate">{slide.title}</h3>
                      <Badge variant={slide.is_active ? "default" : "secondary"}>
                        {slide.is_active ? "Active" : "Inactive"}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        Order: {slide.sort_order}
                      </span>
                    </div>
                    {slide.subtitle && (
                      <p className="text-sm text-muted-foreground mb-1">{slide.subtitle}</p>
                    )}
                    {slide.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {slide.description}
                      </p>
                    )}
                    {slide.cta_text && (
                      <div className="mt-2">
                        <Badge variant="outline">
                          {slide.cta_text} → {slide.cta_link}
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEdit(slide)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDeleteClick(slide.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      <HeroSlideFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        slide={selectedSlide}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the hero slide.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminHeroSlides;
